/**
 * ConversationView
 *
 * 친구와의 대화 뷰어 (FriendConversationModal과 동일한 UI)
 * - 프로필 이미지에서 추출한 색상으로 말풍선 배경 적용
 */

import React, { useMemo, useState, useEffect } from 'react';
import { Box, Typography, Stack, Avatar, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../../hooks/useIsMobile';
import {
  extractDominantColor,
  getContrastingTextColors,
  createGradientBackground,
} from '../../utils/colorExtraction';
import { getPublicAvatarUrl } from '../../utils/publicAvatarUtils';
import moment from 'moment';

/**
 * 대화를 threadId로 그루핑
 */
const groupConversationsByThread = (conversations) => {
  if (!conversations || !Array.isArray(conversations)) return [];

  const grouped = conversations.reduce((acc, conversation) => {
    const threadId = conversation.threadId || conversation.threadText || 'unknown';
    if (!acc[threadId]) {
      acc[threadId] = {
        threadId,
        threadText: conversation.threadText,
        conversations: [],
      };
    }
    acc[threadId].conversations.push(conversation);
    return acc;
  }, {});

  // 각 그룹 내에서 시간순 정렬 후 그룹을 배열로 변환
  return Object.values(grouped)
    .map((group) => ({
      ...group,
      conversations: group.conversations.sort(
        (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
      ),
    }))
    .sort((a, b) => {
      // 각 그룹의 가장 최신 댓글 기준으로 정렬
      const latestA = Math.max(
        ...a.conversations.map((c) => new Date(c.timestamp))
      );
      const latestB = Math.max(
        ...b.conversations.map((c) => new Date(c.timestamp))
      );
      return latestB - latestA;
    });
};

/**
 * 첫 번째 문장 추출
 */
const getFirstSentence = (text) => {
  if (!text) return '';
  const sentences = text
    .split(/[\n.?!]/)
    .filter((s) => s.trim().length > 0);
  return sentences.length > 0 ? sentences[0].trim() : text;
};

export const ConversationView = ({ friend, preloadedFriendColor, preloadedOwnerColor, ownerInfo }) => {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile('xl');

  // 친구 프로필 이미지에서 추출한 색상 (preloadedFriendColor가 있으면 바로 사용)
  const [friendColor, setFriendColor] = useState(() => {
    if (preloadedFriendColor && !preloadedFriendColor.isLoading) {
      return preloadedFriendColor;
    }
    return {
      bgColor: 'action.hover',
      bgGradient: null,
      textColor: 'inherit',
      isLoading: true,
    };
  });

  // 리포트 주인(owner)의 색상 (preloadedOwnerColor가 있으면 바로 사용)
  const [ownerColor, setOwnerColor] = useState(() => {
    if (preloadedOwnerColor && !preloadedOwnerColor.isLoading) {
      return preloadedOwnerColor;
    }
    return {
      bgColor: 'action.selected',
      bgGradient: null,
      textColor: 'inherit',
      isLoading: true,
    };
  });

  // 친구 userId 추출
  const friendUserId = friend?.userId || friend?.user_id || friend?.id || '';

  // 친구 정보 추출
  const friendProfileImage = friend?.profileImage;
  const friendUsername = friend?.username;

  // 친구 아바타 URL (profileImage가 없으면 getPublicAvatarUrl로 fallback)
  const friendAvatarUrl = useMemo(() => {
    // 1순위: friend.profileImage (Firestore에 저장된 값)
    if (friendProfileImage) {
      return friendProfileImage;
    }
    // 2순위: getPublicAvatarUrl로 직접 생성
    if (friendUserId) {
      return getPublicAvatarUrl(friendUserId);
    }
    return null;
  }, [friendProfileImage, friendUserId]);

  // 친구 프로필 색상 추출 (Firebase Storage 캐시된 이미지 사용 - CORS 지원)
  useEffect(() => {
    // preloadedFriendColor가 이미 있으면 추출 건너뛰기
    if (preloadedFriendColor && !preloadedFriendColor.isLoading) {
      setFriendColor(preloadedFriendColor);
      return;
    }

    // friendUserId 또는 profileImage가 없으면 기본 색상 사용
    if (!friendUserId && !friendProfileImage) {
      setFriendColor({ bgColor: 'action.hover', bgGradient: null, textColor: 'inherit', isLoading: false });
      return;
    }

    const extractColor = async () => {
      try {
        // 1순위: Firebase Storage에 캐시된 이미지 URL 사용 (CORS 지원)
        if (friendUserId) {
          const cachedAvatarUrl = getPublicAvatarUrl(friendUserId, 160);

          if (cachedAvatarUrl) {
            // Firebase Storage 이미지 존재 여부 확인
            const response = await fetch(cachedAvatarUrl, { method: 'HEAD' });

            if (response.ok) {
              const colorData = await extractDominantColor(cachedAvatarUrl);
              const textColors = getContrastingTextColors(colorData.isDark);
              // 그라데이션 배경 생성 (ParticipantShowcaseCard와 동일)
              const gradient = createGradientBackground(colorData.color, colorData.isDark);

              // 유효성 검증
              const isValidHex = /^#([A-Fa-f0-9]{6})$/.test(colorData.color);
              const isValidGradient = gradient && typeof gradient === 'string' && gradient.includes('linear-gradient');

              setFriendColor({
                bgColor: isValidHex ? colorData.color : 'action.hover',
                bgGradient: isValidGradient ? gradient : null,
                textColor: textColors.primary,
                secondaryColor: textColors.secondary,
                isDark: colorData.isDark,
                isLoading: false,
              });
              return;
            }
          }
        }

        // 2순위: username 기반 색상 생성 (캐시된 이미지가 없을 경우)
        if (friendUsername) {
          const hash = friendUsername.split('').reduce((acc, char) => {
            return char.charCodeAt(0) + ((acc << 5) - acc);
          }, 0);

          // 파스텔 톤의 색상 생성
          const hue = Math.abs(hash) % 360;
          const saturation = 60 + (Math.abs(hash >> 8) % 20); // 60-80%
          const lightness = 75 + (Math.abs(hash >> 16) % 10); // 75-85%

          const bgColor = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
          const isDark = lightness < 50;
          const textColors = getContrastingTextColors(isDark);

          setFriendColor({
            bgColor,
            bgGradient: null, // username 기반은 그라데이션 없음
            textColor: textColors.primary,
            secondaryColor: textColors.secondary,
            isDark,
            isLoading: false,
          });
          return;
        }

        // 기본 색상
        setFriendColor({ bgColor: 'action.hover', bgGradient: null, textColor: 'inherit', isLoading: false });
      } catch (error) {
        console.error('Failed to extract friend color:', error);
        // 에러 시 username 기반 색상으로 폴백
        if (friendUsername) {
          const hash = friendUsername.split('').reduce((acc, char) => {
            return char.charCodeAt(0) + ((acc << 5) - acc);
          }, 0);
          const hue = Math.abs(hash) % 360;
          setFriendColor({
            bgColor: `hsl(${hue}, 65%, 80%)`,
            bgGradient: null,
            textColor: '#000000',
            secondaryColor: 'rgba(0, 0, 0, 0.6)',
            isDark: false,
            isLoading: false,
          });
        } else {
          setFriendColor({ bgColor: 'action.hover', bgGradient: null, textColor: 'inherit', isLoading: false });
        }
      }
    };

    extractColor();
  }, [friendUserId, friendProfileImage, friendUsername, preloadedFriendColor]);

  // 리포트 주인(owner)의 userId 추출
  const ownerUserId = ownerInfo?.id || '';

  // Firebase Storage 캐시된 주인 아바타 URL (CORS 지원)
  const ownerAvatarUrl = useMemo(() => {
    if (ownerUserId) {
      return getPublicAvatarUrl(ownerUserId);
    }
    return null;
  }, [ownerUserId]);

  // 리포트 주인(owner)의 색상 추출 (Firebase Storage 캐시된 이미지 사용 - CORS 지원)
  useEffect(() => {
    // preloadedOwnerColor가 이미 있으면 추출 건너뛰기
    if (preloadedOwnerColor && !preloadedOwnerColor.isLoading) {
      setOwnerColor(preloadedOwnerColor);
      return;
    }

    const ownerProfileImage = ownerInfo?.threads_profile_picture_url;
    const ownerUsername = ownerInfo?.username;

    if (!ownerUserId && !ownerProfileImage) {
      setOwnerColor({ bgColor: 'action.selected', bgGradient: null, textColor: 'inherit', isLoading: false });
      return;
    }

    const extractColor = async () => {
      try {
        // 1순위: Firebase Storage에 캐시된 이미지 URL 사용 (CORS 지원)
        if (ownerUserId) {
          const cachedAvatarUrl = getPublicAvatarUrl(ownerUserId, 160);

          if (cachedAvatarUrl) {
            // Firebase Storage 이미지 존재 여부 확인
            const response = await fetch(cachedAvatarUrl, { method: 'HEAD' });

            if (response.ok) {
              const colorData = await extractDominantColor(cachedAvatarUrl);
              const textColors = getContrastingTextColors(colorData.isDark);
              // 그라데이션 배경 생성 (ParticipantShowcaseCard와 동일)
              const gradient = createGradientBackground(colorData.color, colorData.isDark);

              // 유효성 검증
              const isValidHex = /^#([A-Fa-f0-9]{6})$/.test(colorData.color);
              const isValidGradient = gradient && typeof gradient === 'string' && gradient.includes('linear-gradient');

              setOwnerColor({
                bgColor: isValidHex ? colorData.color : 'action.selected',
                bgGradient: isValidGradient ? gradient : null,
                textColor: textColors.primary,
                secondaryColor: textColors.secondary,
                isDark: colorData.isDark,
                isLoading: false,
              });
              return;
            }
          }
        }

        // 2순위: username 기반 색상 생성 (캐시된 이미지가 없을 경우)
        if (ownerUsername) {
          const hash = ownerUsername.split('').reduce((acc, char) => {
            return char.charCodeAt(0) + ((acc << 5) - acc);
          }, 0);

          // 파스텔 톤의 색상 생성 (주인 색상은 약간 다른 톤)
          const hue = Math.abs(hash) % 360;
          const saturation = 50 + (Math.abs(hash >> 8) % 15); // 50-65%
          const lightness = 80 + (Math.abs(hash >> 16) % 10); // 80-90%

          const bgColor = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
          const isDark = lightness < 50;
          const textColors = getContrastingTextColors(isDark);

          setOwnerColor({
            bgColor,
            bgGradient: null, // username 기반은 그라데이션 없음
            textColor: textColors.primary,
            secondaryColor: textColors.secondary,
            isDark,
            isLoading: false,
          });
          return;
        }

        // 기본 색상
        setOwnerColor({ bgColor: 'action.selected', bgGradient: null, textColor: 'inherit', isLoading: false });
      } catch (error) {
        console.error('Failed to extract owner color:', error);
        // 에러 시 username 기반 색상으로 폴백
        if (ownerUsername) {
          const hash = ownerUsername.split('').reduce((acc, char) => {
            return char.charCodeAt(0) + ((acc << 5) - acc);
          }, 0);
          const hue = Math.abs(hash) % 360;
          setOwnerColor({
            bgColor: `hsl(${hue}, 55%, 85%)`,
            bgGradient: null,
            textColor: '#000000',
            secondaryColor: 'rgba(0, 0, 0, 0.6)',
            isDark: false,
            isLoading: false,
          });
        } else {
          setOwnerColor({ bgColor: 'action.selected', bgGradient: null, textColor: 'inherit', isLoading: false });
        }
      }
    };

    extractColor();
  }, [ownerUserId, ownerInfo?.threads_profile_picture_url, ownerInfo?.username, preloadedOwnerColor]);

  const threadGroups = useMemo(() => {
    return groupConversationsByThread(friend?.conversations);
  }, [friend?.conversations]);

  if (!friend) return null;

  const {
    username,
    interactionCount = 0,
  } = friend;

  return (
    <Box sx={{ p: 3 }}>
      {/* 헤더 */}
      <Stack spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Avatar
          src={friendAvatarUrl || undefined}
          alt={username}
          sx={{ width: 64, height: 64 }}
        >
          {username?.charAt(0).toUpperCase()}
        </Avatar>
        <Typography variant="h6" fontWeight={600}>
          @{username}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('yearEnd.conversation.totalInteractions', { count: interactionCount })}
        </Typography>
      </Stack>

      <Divider sx={{ mb: 3 }} />

      {/* 대화 목록 (스레드별 그루핑) */}
      {threadGroups.length > 0 ? (
        <Stack spacing={0}>
          {threadGroups.map((threadGroup, groupIndex) => {
            const isLastItem = groupIndex === threadGroups.length - 1;

            return (
              <Box
                key={threadGroup.threadId || groupIndex}
                sx={{
                  position: 'relative',
                  pl: '48px',
                  pb: isLastItem ? 0 : 4,
                  mb: isLastItem ? 0 : 4,
                  borderBottom: isLastItem ? 'none' : '1px solid',
                  borderColor: 'divider',
                }}
              >
                {/* 타임라인 원 마크 */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: '8px',
                    top: '8px',
                    width: 14,
                    height: 14,
                    bgcolor: 'text.primary',
                    borderRadius: '50%',
                    border: '2px solid white',
                    boxShadow: '0 0 0 2px',
                    color: 'text.primary',
                    zIndex: 2,
                  }}
                />

                {/* 타임라인 연결선 */}
                {!isLastItem && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: '14px',
                      top: '22px',
                      width: 2,
                      height: 'calc(100% + 40px)',
                      bgcolor: 'divider',
                      zIndex: 1,
                    }}
                  />
                )}

                {/* 원글 제목 */}
                <Typography
                  variant={isMobile ? 'subtitle1' : 'h6'}
                  fontWeight={600}
                  sx={{ mb: 0.5, wordBreak: 'break-word' }}
                >
                  {getFirstSentence(threadGroup.threadText?.replace('...', ''))}
                </Typography>

                {/* 원글 날짜 */}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 2, display: 'block' }}
                >
                  {threadGroup.conversations?.[0]?.timestamp
                    ? moment(threadGroup.conversations[0].timestamp).locale(i18n.language).format(t('yearEnd.conversation.dateFormat'))
                    : ''}
                </Typography>

                {/* 해당 원글의 대화들 */}
                <Stack spacing={1.5}>
                  {threadGroup.conversations.map((conversation, index) => {
                    const isMyComment = conversation.type === 'my_reply';

                    return (
                      <Box
                        key={conversation.comment?.id || index}
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'flex-start',
                          gap: 1.5,
                          justifyContent: isMyComment ? 'flex-end' : 'flex-start',
                        }}
                      >
                        {/* 상대방 댓글일 때 왼쪽 아바타 */}
                        {!isMyComment && (
                          <Avatar
                            src={friendAvatarUrl || undefined}
                            sx={{ width: 32, height: 32, fontSize: '0.75rem' }}
                          >
                            {username?.substring(0, 2).toUpperCase()}
                          </Avatar>
                        )}

                        {/* 메시지 박스 - 프로필 색상 적용 (그라데이션 우선) */}
                        <Box
                          component="a"
                          href={conversation.comment?.permalink || '#'}
                          target="_blank"
                          rel="noreferrer"
                          sx={{
                            maxWidth: '75%',
                            p: 1.5,
                            borderRadius: 2,
                            // 그라데이션 우선, 없으면 단색 사용 (ParticipantShowcaseCard와 동일)
                            background: isMyComment
                              ? (ownerColor.isLoading ? undefined : (ownerColor.bgGradient || ownerColor.bgColor))
                              : (friendColor.isLoading ? undefined : (friendColor.bgGradient || friendColor.bgColor)),
                            bgcolor: isMyComment
                              ? (ownerColor.isLoading ? 'action.selected' : undefined)
                              : (friendColor.isLoading ? 'action.hover' : undefined),
                            textDecoration: 'none',
                            color: isMyComment
                              ? (ownerColor.isLoading ? 'inherit' : ownerColor.textColor)
                              : (friendColor.isLoading ? 'inherit' : friendColor.textColor),
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              boxShadow: 2,
                              filter: 'brightness(0.95)',
                            },
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              wordBreak: 'break-word',
                              lineHeight: 1.5,
                              color: 'inherit',
                            }}
                          >
                            {conversation.comment?.text || t('yearEnd.conversation.noContent')}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              mt: 0.5,
                              textAlign: 'right',
                              color: isMyComment
                                ? (ownerColor.isLoading ? 'text.disabled' : ownerColor.secondaryColor || 'inherit')
                                : (friendColor.isLoading ? 'text.disabled' : friendColor.secondaryColor || 'inherit'),
                              opacity: 0.8,
                            }}
                          >
                            {conversation.timestamp
                              ? moment(conversation.timestamp).format('MM/DD HH:mm')
                              : ''}
                          </Typography>
                        </Box>

                        {/* 리포트 주인 댓글일 때 오른쪽 아바타 */}
                        {isMyComment && (
                          <Avatar
                            src={ownerAvatarUrl || undefined}
                            sx={{
                              width: 32,
                              height: 32,
                              fontSize: '0.7rem',
                              bgcolor: 'grey.200',
                              color: 'text.primary',
                            }}
                          >
                            {ownerInfo?.username?.substring(0, 2).toUpperCase() || t('yearEnd.conversation.me')}
                          </Avatar>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      ) : (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">
            {t('yearEnd.conversation.noConversation')}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ConversationView;
